import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import StorageIcon from "@mui/icons-material/Storage";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { useTranslation } from "react-i18next";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useAppSelector } from "@/store";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@components/PageHeader";
import { ScraperStatusChip } from "@components/ScraperStatusChip";
import { useDashboardBootstrap } from "@hooks/useDashboardBootstrap";
import {
  DASHBOARD_CHART_BAR_COLOR,
  DASHBOARD_STAT_COLORS,
} from "@/constants/dashboard";

export default function Dashboard() {
  const { t } = useTranslation();
  useDashboardBootstrap();
  const navigate = useNavigate();
  const stats = useAppSelector((s) => s.scrapers.stats);
  const scrapers = useAppSelector((s) => s.scrapers.list);
  const recentActivity = useAppSelector((s) => s.history.recentActivity);

  const statCards = [
    {
      label: t("dashboard.activeScrapers"),
      value: stats ? `${stats.activeScrapers}/${stats.maxScrapers}` : "-",
      icon: <LanguageIcon />,
      color: DASHBOARD_STAT_COLORS.scrapers,
    },
    {
      label: t("dashboard.dataExtracted"),
      value: stats
        ? `${stats.totalExtracted.toLocaleString()} ${t("dashboard.rows")}`
        : "-",
      icon: <StorageIcon />,
      color: DASHBOARD_STAT_COLORS.extracted,
    },
    {
      label: t("dashboard.successRate"),
      value: stats ? `${stats.successRate}%` : "-",
      icon: <CheckCircleIcon />,
      color: DASHBOARD_STAT_COLORS.success,
    },
    {
      label: t("dashboard.recentErrors"),
      value: stats ? String(stats.recentErrors) : "-",
      icon: <ErrorIcon />,
      color: DASHBOARD_STAT_COLORS.errors,
    },
  ];

  const chartData = recentActivity
    .slice()
    .reverse()
    .map((h) => ({
      date: new Date(h.date).toLocaleDateString(),
      amount: h.amount,
    }));

  return (
    <Box>
      <PageHeader>{t("dashboard.title")}</PageHeader>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {statCards.map((card) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={card.label}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Box sx={{ color: card.color }}>{card.icon}</Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    {card.label}
                  </Typography>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: "bold",
                  }}
                >
                  {card.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  mb: 2,
                }}
              >
                {t("dashboard.extractionVolume")}
              </Typography>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip />
                  <Bar
                    dataKey="amount"
                    fill={DASHBOARD_CHART_BAR_COLOR}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  mb: 2,
                }}
              >
                {t("dashboard.recentActivity")}
              </Typography>
              {recentActivity.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  {t("dashboard.noActivity")}
                </Typography>
              ) : (
                <List dense disablePadding>
                  {recentActivity.slice(0, 8).map((h) => (
                    <ListItem key={h.id} disablePadding sx={{ mb: 1 }}>
                      <ListItemText
                        primary={h.scraperName}
                        secondary={new Date(h.date).toLocaleString()}
                        slotProps={{
                          primary: {
                            sx: {
                              fontSize: 13,
                              fontWeight: 500,
                            },
                          },
                          secondary: { sx: { fontSize: 11 } },
                        }}
                      />
                      <Chip
                        label={
                          h.success ? `${h.amount}` : t("dashboard.runFailed")
                        }
                        size="small"
                        color={h.success ? "success" : "error"}
                        sx={{ fontSize: 11 }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              mb: 2,
            }}
          >
            {t("dashboard.activeScrapersTable")}
          </Typography>
          <TableContainer component={Paper} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("scrapers.name")}</TableCell>
                  <TableCell>{t("scrapers.targetUrl")}</TableCell>
                  <TableCell>{t("scrapers.status")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scrapers.slice(0, 5).map((s) => (
                  <TableRow
                    key={s.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate("/scrapers")}
                  >
                    <TableCell>{s.name}</TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 200,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {s.base}
                    </TableCell>
                    <TableCell>
                      <ScraperStatusChip
                        status={s.status}
                        label={t(`scrapers.${s.status}`)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}
